import { FC } from "react";

import { Tooltip, useDisclosure } from "@chakra-ui/react";
import { theme } from "@ovou-inc/ovou-ui";

import { Box } from "../Container";

/**
 * A small info (i) icon with a dark tooltip — a reusable primitive for section
 * headings and labels across the app. The icon matches the design system's
 * circled-i (geometric, black). By default it self-manages on hover. Pass
 * `isOpen` + `onActivate` to control it externally: `onActivate` fires when the
 * icon is hovered or clicked, and `isOpen` decides visibility — letting a parent
 * keep the tooltip open until the cursor leaves the surrounding section.
 */
const InfoTooltip: FC<{
  label: string;
  isOpen?: boolean;
  onActivate?: () => void;
}> = ({ label, isOpen: controlledOpen, onActivate }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const isControlled = controlledOpen !== undefined;

  return (
    <Tooltip
      label={label}
      isOpen={isControlled ? controlledOpen : isOpen}
      hasArrow
      placement="bottom-start"
      bg={theme.palette.brand.primary.black}
      color={theme.palette.brand.primary.white}
      borderRadius="8px"
      px="12px"
      py="8px"
      fontSize="13px"
      fontWeight={400}
      maxW="460px"
      textAlign="left"
    >
      <Box
        as="span"
        display="inline-flex"
        alignItems="center"
        aria-label="More info"
        onMouseEnter={isControlled ? onActivate : onOpen}
        onMouseLeave={isControlled ? undefined : onClose}
        onClick={isControlled ? onActivate : undefined}
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2ZM0 10C0 4.47715 4.47715 0 10 0C15.5228 0 20 4.47715 20 10C20 15.5228 15.5228 20 10 20C4.47715 20 0 15.5228 0 10ZM10 9C10.5523 9 11 9.44771 11 10V13.6C11 14.1523 10.5523 14.6 10 14.6C9.44771 14.6 9 14.1523 9 13.6V10C9 9.44771 9.44771 9 10 9ZM10 5.3999C9.44771 5.3999 9 5.84762 9 6.3999C9 6.95219 9.44771 7.3999 10 7.3999H10.009C10.5613 7.3999 11.009 6.95219 11.009 6.3999C11.009 5.84762 10.5613 5.3999 10.009 5.3999H10Z"
            fill={theme.palette.brand.primary.black}
          />
        </svg>
      </Box>
    </Tooltip>
  );
};

export default InfoTooltip;
